import { useEffect } from 'react'
import { Alert, DatePicker, Form, Input, Modal } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { tenantToday } from '@/shared/lib/dayRange'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { NAME_MAX, REASON_MAX } from './hrApi'
import { ACCOUNT_NO_MAX, ACCOUNT_NO_MIN } from './payApi'
import { setBankAccount, type BankAccount } from './bankApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

const DATE_FORMAT = 'YYYY-MM-DD'

interface Props {
  open: boolean
  personId: string
  /** Joriy rekvizit — yangi davr uni yopadi. `null` bo'lsa birinchisi. */
  current: BankAccount | null
  onClose: () => void
}

interface FormValues {
  bankName: string
  accountNo: string
  holderName?: string
  validFrom: Dayjs
  reason: string
}

/**
 * Bank rekvizitini belgilash — YANGI DAVR ochadi.
 *
 * <h2>⚠️ TAHRIRLASH EMAS</h2>
 * Backendda `PUT` yo'q va bo'lmaydi ham: eski rekvizit maosh qaysi
 * hisobga ketganini isbotlaydi. Ustiga yozish o'sha javobni yo'q
 * qilardi. Shuning uchun oyna nomi ham «tahrirlash» emas.
 *
 * <h2>⚠️ HISOB RAQAMI SHAKLI TEKSHIRILMAYDI</h2>
 * Server faqat uzunlikni qaraydi (4..64) va bu ONGLI: IBAN, 16 raqamli
 * karta va bankning ichki raqami — uchalasi ham to'g'ri. Shaklni
 * qotirish birinchi boshqa bankda ishni to'xtatardi.
 *
 * <h2>⚠️ XATO OYNANING ICHIDA (`guards.mjs` 1-bo'limi)</h2>
 * Yopishda `save.reset()`.
 */
export function BankAccountModal({ open, personId, current, onClose }: Props) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      // ⚠️ Bank nomi oldindan to'ldiriladi (odatda o'sha bank), raqam
      // esa YO'Q: uni nusxa qilib qoldirish «o'zgartirdim» degan
      // taassurot bilan eski raqamni qaytadan yozardi
      bankName: current?.bankName ?? '',
      accountNo: '',
      holderName: current?.holderName ?? '',
      validFrom: tenantToday(),
      reason: '',
    })
  }, [open, current, form])

  const save = useMutation({
    mutationFn: (values: FormValues) =>
      setBankAccount(personId, {
        bankName: values.bankName.trim(),
        accountNo: values.accountNo.trim(),
        holderName: values.holderName?.trim() ? values.holderName.trim() : null,
        validFrom: values.validFrom.format(DATE_FORMAT),
        reason: values.reason.trim(),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.hr.bankAccounts(personId) })
      form.resetFields()
      onClose()
    },
  })

  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={t('hr.bank.setTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={t('common.save')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      <div style={{ marginBottom: 16 }}>
        <ExpandableHint text={t('hr.bank.setNote')} fontSize={13} />
      </div>

      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="bankName"
          label={t('hr.bank.bankName')}
          rules={[
            { required: true, message: t('hr.bank.validation.bankNameRequired') },
            { max: NAME_MAX, message: t('hr.person.validation.nameLength') },
          ]}
        >
          <Input autoFocus />
        </Form.Item>

        <Form.Item
          name="accountNo"
          label={t('hr.bank.accountNo')}
          extra={t('hr.bank.accountNoHint')}
          rules={[
            { required: true, message: t('hr.bank.validation.accountNoRequired') },
            {
              min: ACCOUNT_NO_MIN,
              max: ACCOUNT_NO_MAX,
              message: t('hr.bank.validation.accountNoLength'),
            },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="holderName"
          label={t('hr.bank.holderName')}
          extra={t('hr.bank.holderNameHint')}
          rules={[{ max: NAME_MAX, message: t('hr.person.validation.nameLength') }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="validFrom"
          label={t('hr.bank.validFrom')}
          extra={t('hr.bank.validFromHint')}
          rules={[{ required: true, message: t('hr.salary.validation.validFromRequired') }]}
        >
          <DatePicker format={DATE_FORMAT} style={{ width: '100%' }} />
        </Form.Item>

        <Form.Item
          name="reason"
          label={t('hr.bank.reason')}
          extra={t('hr.bank.reasonHint')}
          rules={[
            { required: true, message: t('hr.salary.validation.reasonRequired') },
            { max: REASON_MAX, message: t('hr.assignment.validation.reasonLength') },
          ]}
        >
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
